import { TODO } from "@habity/types";

export function mergeRecursive(src: TODO, next: TODO) {
  let k;
  if (src && next && typeof src === "object" && typeof next === "object") {
    if (Array.isArray(next)) {
      for (k = 0; k < next.length; k++) {
        src[k] = mergeRecursive(src[k], next[k]);
      }
    } else {
      for (k in next) {
        src[k] = mergeRecursive(src[k], next[k]);
      }
    }
    return src;
  }
  return next;
}

export function deepMerge(target: TODO, ...args: TODO) {
  const len: number = args.length;
  for (let i = 0; i < len; i++) {
    target = mergeRecursive(target, args[i]);
  }
  return target;
}

/**
 * Take a mustache template with placeholders in the format {{x}}
 * And replace the variables with values from the "data" property
 * Source https://stackoverflow.com/a/61634647/811799
*/
export function renderMustacheTemplate(string: string, data: Record<string, string>) {
  return string.replace(
    /{{(\w+)}}/g,
    (placeholderWithDelimiters, placeholderWithoutDelimiters) =>
      data.hasOwnProperty(placeholderWithoutDelimiters)
        ? data[placeholderWithoutDelimiters]
        : placeholderWithDelimiters
  );
}

export const omitNullKeys = (obj: TODO) => {
  const result =
    obj !== null && typeof obj === "object"
      ? Object.fromEntries(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Object.entries(obj).filter(([_, v]) => v != null && v !== "")
      )
      : null;
  return result && Object.keys(result).length > 0 ? result : null;
};

export const capitalizeFirstLetter = (str: string, lower = false) =>
  str.charAt(0).toUpperCase() + (lower ? str.toLowerCase() : str).slice(1);

export const capitalizeAllWords = (str: string, lower = false) =>
  (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, (match) =>
    match.toUpperCase()
  );

export function getRandomElement<T>(arr: T[]) {
  return arr[Math.floor(Math.random() * arr.length)];
};

export const getRandomString = (length: number) => {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};

export const groupBy = function <T>(xs: T[], key: string): Record<string, T[]> {
  return xs.reduce(function (rv: TODO, x: TODO) {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

// Why is this needed? I've always done [...new Set(arr)];
export const removeDuplicates = <T>(arr: T[]) => {
  return arr.filter((v, i, a) => a.indexOf(v) === i);
};

export const parseJSONWithCatch = (str: string) => {
  try {
    const parsed = JSON.parse(str.toString());
    return parsed;
  } catch (e) {
    return str;
  }
};

export const cleanStreetName = (street: string): string => {
  if (!street) return "";

  let cleaned = street.replace(/\s+\d+.*$/, "");
  cleaned = cleaned.replace(/\s*,.*$/, "");
  return cleaned.trim().toLowerCase();
};

export const swedishToAscii = (text: string): string => {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/å/g, "a")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/Å/g, "a")
    .replace(/Ä/g, "a")
    .replace(/Ö/g, "o");
};
